import { withErrorBoundary } from "@sentry/react";
import a from "indefinite";
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  ArtifactActivityFragmentFragment,
  ArtifactActivityType,
  GetArtifactSidebarQueryQuery,
  GoalArtifactSidebarFragmentFragment,
  KeyResultNode,
  ProfileArtifactActivityFragmentFragment,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import ActivityArtifactKeyResultUpdated from "@apps/activities/components/artifact-key-result-updated";
import ActivityArtifactReferencedFromArtifact from "@apps/activities/components/artifact-referenced-from-artifact";
import ActivityArtifactReferencedFromTopic from "@apps/activities/components/artifact-referenced-from-topic";
import ActivityCommentAdded from "@apps/activities/components/comment-added";
import ActivityGoalCheckin from "@apps/activities/components/goal-checkin";
import useLabel from "@apps/use-label/use-label";
import ErrorContainer from "@components/error/error";
import AppLink from "@components/link/link";
import TimeAgoCustom from "@components/time-ago/time-ago";
import { classNames } from "@helpers/css";
import { getUrl, toWithBackground } from "@helpers/helpers";

import ActivityIcon from "./artifact-activity-icon";
import ActivityArtifactUpdate from "./artifact-update";

const ArtifactActivityComponent = ({
  artifact,
  activity,
  hideLeftIcon = false,
  mentionArtifactName = false,
}: {
  artifact:
    | GetArtifactSidebarQueryQuery["artifact"]
    | GoalArtifactSidebarFragmentFragment
    | NonNullable<ProfileArtifactActivityFragmentFragment["artifact"]>;
  activity: ArtifactActivityFragmentFragment;
  hideLeftIcon?: boolean;
  mentionArtifactName?: boolean;
}) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const location = useLocation<TFLocationState>();
  const { search } = location;
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const matchingUrlActivityId =
    queryParams.get("activityId") === String(activity.id);
  const { activityType } = activity;
  const label = useLabel();
  let content = null;
  let actionLabel: string | JSX.Element = String(activity.activityType);
  const artifactTitle =
    artifact.__typename === "DecisionArtifactNode"
      ? artifact.decision
      : artifact.__typename === "RecognitionArtifactNode"
        ? a(label("recognition"))
        : artifact.title;
  const artifactLabel = mentionArtifactName ? (
    <AppLink
      to={toWithBackground({
        pathname: getUrl({
          artifactId: artifact.id,
          artifactType: artifact.artifactType,
        }),
        location,
      })}
      className="text-blue-link hover:underline"
    >
      {artifactTitle}
    </AppLink>
  ) : (
    `this ${label(artifact.artifactType)}`
  );

  const firstChangedField = activity.changedFields?.[0];
  if (activityType === ArtifactActivityType.ArtifactCreation) {
    actionLabel = <span>created {artifactLabel}</span>;
  } else if (
    activityType === ArtifactActivityType.KeyResultAdded &&
    firstChangedField
  ) {
    const keyResult = firstChangedField.newValueObject as KeyResultNode;
    actionLabel = (
      <span>
        added the {label("key result")}:{" "}
        <span className="font-medium text-gray-700">{keyResult.title}</span>
      </span>
    );
  } else if (
    activityType === ArtifactActivityType.KeyResultRemoved &&
    firstChangedField
  ) {
    const keyResult = firstChangedField.previousValueObject as KeyResultNode;
    actionLabel = (
      <span>
        removed the {label("key result")}:{" "}
        <span className="font-medium text-gray-700">{keyResult.title}</span>
      </span>
    );
  } else if (
    activityType === ArtifactActivityType.KeyResultUpdated &&
    firstChangedField
  ) {
    const keyResult = firstChangedField.newValueObject as KeyResultNode;
    actionLabel = (
      <span>
        updated the {label("key result")}:{" "}
        <span className="font-medium text-gray-700">{keyResult.title}</span>
      </span>
    );
    content = (
      <ActivityArtifactKeyResultUpdated
        activity={activity}
        artifactId={artifact.id}
      />
    );
  } else if (
    activityType === ArtifactActivityType.ArtifactUpdate &&
    firstChangedField
  ) {
    actionLabel = <span>updated {artifactLabel}</span>;
    content = (
      <ActivityArtifactUpdate activity={activity} artifactId={artifact.id} />
    );
  } else if (activityType === ArtifactActivityType.ReferencedFromTopic) {
    actionLabel = <span>mentioned {artifactLabel} in the topic</span>;
    content = <ActivityArtifactReferencedFromTopic activity={activity} />;
  } else if (activityType === ArtifactActivityType.ReferencedFromArtifact) {
    const newValueObj = activity?.changedFields?.[0]?.newValueObject;
    actionLabel = (
      <span>
        mentioned {artifactLabel} in the{" "}
        {newValueObj && newValueObj.__typename === "BaseArtifactNode"
          ? label(newValueObj.artifactType)
          : ""}
      </span>
    );
    content = <ActivityArtifactReferencedFromArtifact activity={activity} />;
  } else if (
    activityType === ArtifactActivityType.GoalCheckin &&
    artifact.__typename === "GoalArtifactNode"
  ) {
    actionLabel = <span>updated {artifactLabel}</span>;
    content = <ActivityGoalCheckin activity={activity} artifact={artifact} />;
  } else if (activityType === ArtifactActivityType.CommentAdded) {
    actionLabel = <span>added a comment</span>;
    content = <ActivityCommentAdded activity={activity} artifact={artifact} />;
  } else if (
    activityType === ArtifactActivityType.CompetencyConnected ||
    activityType === ArtifactActivityType.CompetencyDisconnected
  ) {
    const isConnected =
      activityType === ArtifactActivityType.CompetencyConnected;
    const valueSource = isConnected ? "newValueObject" : "previousValueObject";

    const competencyField = activity.changedFields?.find(
      (field) => field?.fieldName === "competency",
    );
    const roleField = activity.changedFields?.find(
      (field) => field?.fieldName === "career_track_role",
    );
    const competency = competencyField?.[valueSource] as any;
    const role = roleField?.[valueSource] as any;
    const competencyTitle =
      competency?.__typename === "CompetencyNode"
        ? competency.title
        : a(label("competency"));
    const roleName =
      role?.__typename === "CareerTrackRoleNode" ? role.title : null;

    actionLabel = (
      <span>
        {isConnected ? "connected" : "disconnected"} {artifactLabel}{" "}
        {isConnected ? "to" : "from"} the {label("competency")}{" "}
        <span className="font-medium text-gray-700">{competencyTitle}</span>
        {roleName && (
          <span>
            {" "}
            for the{" "}
            <span className="font-medium text-gray-700">{roleName}</span> role
          </span>
        )}
      </span>
    );
  } else {
    throw new Error(`activityType not handled: ${activityType}`);
  }

  useEffect(() => {
    if (ref.current && matchingUrlActivityId) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [ref, matchingUrlActivityId]);

  return (
    <div
      className={classNames(
        matchingUrlActivityId &&
          "bg-yellow-50 rounded border border-yellow-200 -m-2 p-2",
      )}
    >
      <div className="relative flex items-start space-x-3" ref={ref}>
        {!hideLeftIcon && (
          <div>
            <div className="relative px-1">
              <div className="mt-1 h-6 w-6 bg-gray-200 rounded-full flex items-center justify-center">
                <ActivityIcon activityType={activityType} />
              </div>
            </div>
          </div>
        )}
        <div className="min-w-0 flex-1 py-1.5">
          <div className="text-sm text-gray-500">
            <span className="font-medium text-gray-700">
              {activity.actor?.name || "A user"}
            </span>{" "}
            {actionLabel}
            <AppLink
              to={toWithBackground({
                pathname: `${getUrl({
                  artifactId: artifact.id,
                  artifactType: artifact.artifactType,
                })}?activityId=${activity.id}`,
                location,
              })}
              className="whitespace-nowrap text-xs tracking-tighter ml-1 text-gray-400 hover:underline"
            >
              (<TimeAgoCustom date={activity.created} />)
            </AppLink>
          </div>
          <div className="text-sm text-gray-800 mt-2 empty:hidden ">
            {content}
          </div>
        </div>
      </div>
    </div>
  );
};

const ArtifactActivityWithSentry = withErrorBoundary(
  ArtifactActivityComponent,
  {
    fallback: (
      <ErrorContainer description={"This activity could not be displayed."} />
    ),
  },
);

export default ArtifactActivityWithSentry;
